import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, Download } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sections = [
  {
    id: "switches",
    label: "Switches",
    items: [
      { slug: "aroma-switches", name: "Aroma", img: "/assets/catalog/aroma.png", fit: "contain", tileFit: "contain", desc: "A simple, reliable switch designed for everyday use. Its clean design and dependable performance make it a widely used choice across the industry." },
      { slug: "eben-switches", name: "Eben", img: "/assets/catalog/eben.png", fit: "contain", tileFit: "contain", desc: "Designed for a seamless modular look, this switch stays perfectly flush in the OFF position and subtly projects outward when switched ON. Its innovative flat-to-bulge mechanism combines clean aesthetics with intuitive operation." },
      { slug: "edge-switches", name: "Edge", img: "/assets/catalog/edge.png", fit: "contain", tileFit: "contain", desc: "A clean, borderless switch designed for a seamless, modern appearance. With no visible corners or surrounding borders, it blends effortlessly into the surface for a minimal and refined finish." },
      { slug: "kapsel-switches", name: "Kapsel", img: "/assets/catalog/kapsel.png", fit: "contain", tileFit: "contain", desc: "Kapsel features softly rounded corners that give the switch a smooth, contemporary profile. Its distinctive curved design adds a refined aesthetic while maintaining a clean and functional appearance." },
      { slug: "slim-switches", name: "Slim", img: "/assets/catalog/slim.png", fit: "contain", tileFit: "contain", desc: "Slim features a sleek chrome-finished knob that brings a touch of luxury and precision to every detail. Its slender profile and refined finish make it a sophisticated choice for modern interiors." },
      { slug: "nivel-switches", name: "Nivel", img: "/assets/catalog/nivel.png", fit: "contain", tileFit: "contain", desc: "Nivel stays perfectly flat in both the ON and OFF positions, maintaining a clean, seamless profile. A smartly integrated indicator window provides a clear visual cue of the switch status, combining minimal design with intuitive functionality." },
    ],
  },
  {
    id: "others",
    label: "Others",
    items: [
      { slug: "sockets", name: "Sockets", img: "/assets/catalog/sockets.png", fit: "contain", tileFit: "contain", desc: "A clean and reliable socket designed for everyday use. Its simple profile and sturdy construction provide dependable functionality while maintaining a seamless, modern appearance." },
      { slug: "regulators", name: "Regulators", img: "/assets/catalog/regulators.png", fit: "contain", tileFit: "contain", desc: "A refined rotary regulator designed for smooth, precise control. Its clean profile and intuitive knob bring dependable functionality together with a sophisticated finish." },
      { slug: "plates", name: "Plates", img: "/assets/catalog/plates.png", fit: "contain", tileFit: "contain", desc: "Designed with clean lines and a refined finish, these plates provide a seamless foundation for switches and accessories. Their understated design complements both contemporary and classic interiors." },
      { slug: "accessories", name: "Accessories", img: "/assets/catalog/accessories.png", fit: "contain", tileFit: "contain", desc: "A versatile range of thoughtfully designed accessories that add functionality without compromising on aesthetics. Clean, practical, and designed to integrate seamlessly with modern interiors." },
      { slug: "lights", name: "Lights", img: "/assets/catalog/lights.png", fit: "contain", tileFit: "contain", desc: "Thoughtfully designed lighting solutions that combine functional illumination with distinctive aesthetics. Clean forms and soft, diffused light add character and ambience to modern spaces." },
    ],
  },
];

const tileReveal = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.05 * i },
  }),
};

const NOISE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.4'/%3E%3C/svg%3E\")";

const CatalogueModal = ({ product, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      data-testid="catalog-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      onClick={onClose}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-md"
    >
      <motion.div
        data-lenis-prevent
        data-testid="catalog-modal-panel"
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.97 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[88vh] w-full max-w-md overflow-y-auto border border-white/10 bg-[#111111] text-white shadow-2xl"
      >
        <button
          data-testid="catalog-modal-close"
          onClick={onClose}
          aria-label="Close product preview"
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-200 hover:bg-brand"
        >
          <X size={16} />
        </button>

        <div className="aspect-[4/3] w-full overflow-hidden">
          <img
            src={product.img}
            alt={product.name}
            data-testid="catalog-modal-image"
            className={`h-full w-full ${product.fit === "contain" ? "object-contain" : "object-cover"}`}
          />
        </div>

        <div className="px-6 py-6 sm:px-8 sm:py-7">
          <h2
            data-testid="catalog-modal-title"
            className="font-heading text-2xl font-black tracking-tight"
          >
            {product.name}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-white/55">
            {product.desc ||
              "Manufactured to order against your drawings, ratings and finish requirements. The detailed specification sheet for this range is shared on request."}
          </p>
          <Link
            to="/contact"
            data-testid="catalog-modal-cta"
            onClick={onClose}
            className="mt-6 inline-flex items-center gap-2.5 border border-white/25 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.22em] transition-colors duration-300 hover:border-brand hover:bg-brand"
          >
            Request specification sheet
            <ArrowUpRight size={13} />
          </Link>
        </div>
      </motion.div>
    </motion.div>
  );
};

const DownloadCatalogue = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("phone"); // phone | otp | done
  const [digits, setDigits] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const phone = `+91${digits}`;
  const apiError = async (res) => {
    let msg = "Something went wrong. Please try again.";
    try {
      const data = await res.json();
      if (data?.detail) msg = data.detail;
    } catch {}
    throw new Error(msg);
  };

  const sendOtp = async (isResend = false) => {
    if (!/^[6-9]\d{9}$/.test(digits)) {
      setError("Enter a valid 10-digit mobile number");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/catalogue/request-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      if (!res.ok) await apiError(res);
      setStep("otp");
      setOtp("");
      setResendIn(45);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter the 6-digit code");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/catalogue/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, code: otp }),
      });
      if (!res.ok) await apiError(res);
      const { token } = await res.json();
      window.location.assign(`${API}/catalogue/download?token=${encodeURIComponent(token)}`);
      setStep("done");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setStep("phone");
      setDigits("");
      setOtp("");
      setError("");
      setResendIn(0);
    }, 300);
  };

  const inputCls =
    "w-full border border-white/15 bg-transparent px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-brand";

  return (
    <>
      <button
        data-testid="catalog-download-button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-3 border border-white/25 px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:border-brand hover:bg-brand"
      >
        Download Catalogue
        <Download size={15} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="download-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={close}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-md"
          >
            <motion.div
              data-lenis-prevent
              data-testid="download-modal-panel"
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.97 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md border border-white/10 bg-[#111111] px-6 py-8 text-white shadow-2xl sm:px-10 sm:py-10"
            >
              <button
                data-testid="download-modal-close"
                onClick={close}
                aria-label="Close"
                className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-200 hover:bg-brand"
              >
                <X size={16} />
              </button>

              {step === "done" ? (
                <div className="text-center" data-testid="download-success">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-brand">
                    Verified
                  </p>
                  <h3 className="mt-3 font-heading text-2xl font-black tracking-tight">
                    Your download has started.
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/55">
                    The catalogue PDF is downloading to your device.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-brand">
                    {step === "phone" ? "Download Catalogue" : "Verify OTP"}
                  </p>
                  <h3 className="mt-3 font-heading text-2xl font-black tracking-tight">
                    {step === "phone" ? "Verify your mobile number" : "Enter the code"}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/55">
                    {step === "phone"
                      ? "We will send a one-time password by SMS. The catalogue downloads right after verification."
                      : `A 6-digit code was sent to +91 ${digits.slice(0, 5)} ${digits.slice(5)}.`}
                  </p>

                  {step === "phone" ? (
                    <div className="mt-6">
                      <div className="flex">
                        <span className="flex items-center border border-r-0 border-white/15 px-4 text-sm text-white/60">
                          +91
                        </span>
                        <input
                          data-testid="download-phone-input"
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          value={digits}
                          onChange={(e) => setDigits(e.target.value.replace(/\D/g, "").slice(0, 10))}
                          placeholder="10-digit mobile number"
                          className={inputCls}
                        />
                      </div>
                      <button
                        data-testid="download-send-otp"
                        onClick={() => sendOtp(false)}
                        disabled={loading}
                        className="mt-5 inline-flex w-full items-center justify-center gap-2.5 border border-white/25 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.22em] transition-colors duration-300 hover:border-brand hover:bg-brand disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {loading ? "Sending…" : "Send OTP"}
                      </button>
                    </div>
                  ) : (
                    <div className="mt-6">
                      <input
                        data-testid="download-otp-input"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="••••••"
                        className={`${inputCls} text-center text-lg tracking-[0.6em]`}
                      />
                      <button
                        data-testid="download-verify-button"
                        onClick={verifyOtp}
                        disabled={loading}
                        className="mt-5 inline-flex w-full items-center justify-center gap-2.5 border border-white/25 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.22em] transition-colors duration-300 hover:border-brand hover:bg-brand disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {loading ? "Verifying…" : "Verify & Download"}
                      </button>
                      <div className="mt-4 flex items-center justify-between text-[12px] text-white/40">
                        <button
                          onClick={() => {
                            setStep("phone");
                            setOtp("");
                            setError("");
                          }}
                          className="transition-colors hover:text-white"
                        >
                          Change number
                        </button>
                        {resendIn > 0 ? (
                          <span data-testid="download-resend-timer">Resend in {resendIn}s</span>
                        ) : (
                          <button
                            data-testid="download-resend-button"
                            onClick={() => sendOtp(true)}
                            disabled={loading}
                            className="text-white/70 underline-offset-4 transition-colors hover:text-brand hover:underline disabled:opacity-40"
                          >
                            Resend OTP
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {error && (
                    <p data-testid="download-error" className="mt-4 text-[13px] text-brand">
                      {error}
                    </p>
                  )}
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const Catalog = () => {
  const [active, setActive] = useState(null);

  return (
    <main data-testid="catalog-page" className="bg-black text-white">
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 60% 45% at 50% 30%, rgba(255,255,255,0.05), transparent 70%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: NOISE }}
        />

        <div className="relative mx-auto w-full max-w-[1400px] px-6 pb-16 pt-28 sm:px-10 lg:px-16 lg:pb-20 lg:pt-36">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between"
          >
            <div>
              <p
                className="mb-4 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-brand"
                data-testid="catalog-eyebrow"
              >
                <span className="inline-block h-2 w-2 bg-brand" />
                Products — Catalog
              </p>
              <h1
                className="font-heading text-4xl font-black tracking-tighter sm:text-5xl lg:text-6xl"
                data-testid="catalog-heading"
              >
                What we manufacture.
              </h1>
            </div>
            <div className="flex max-w-xs flex-col items-start gap-6">
              <p className="text-sm leading-relaxed text-white/50">
                Electromechanical switches and wiring accessories, engineered and
                built to your specification.
              </p>
              <DownloadCatalogue />
            </div>
          </motion.div>

          {sections.map((section, s) => (
            <div
              key={section.id}
              data-testid={`catalog-section-${section.id}`}
              className={s === 0 ? "mt-12 lg:mt-16" : "mt-14 lg:mt-16"}
            >
              <div className="flex items-baseline justify-between border-b border-white/10 pb-3">
                <h2 className="flex items-baseline gap-3 font-heading text-xl font-bold tracking-tight sm:text-2xl">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-brand">
                    {String(s + 1).padStart(2, "0")}
                  </span>
                  {section.label}
                </h2>
                <span className="text-[11px] font-medium uppercase tracking-[0.25em] text-white/40">
                  {section.items.length} products
                </span>
              </div>

              <div
                className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-5 lg:grid-cols-7"
                data-testid={`catalog-row-${section.id}`}
              >
                {section.items.map((item, i) => (
                  <motion.button
                    key={item.slug}
                    custom={i}
                    variants={tileReveal}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-40px" }}
                    data-testid={`catalog-cell-${item.slug}`}
                    onClick={() => setActive(item)}
                    aria-label={`View ${item.name}`}
                    className="group text-center"
                  >
                    <span className="block aspect-square w-full overflow-hidden">
                      <img
                        src={item.img}
                        alt={item.name}
                        loading="lazy"
                        className={`h-full w-full ${item.tileFit === "contain" ? "object-contain p-3 sm:p-4" : "object-cover"} grayscale transition-[filter,transform] duration-500 ease-out group-hover:scale-[1.04] group-hover:grayscale-0`}
                      />
                    </span>
                    <span className="mt-3 block font-heading text-sm font-bold tracking-tight transition-colors duration-300 group-hover:text-brand">
                      {item.name}
                    </span>
                  </motion.button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {active && <CatalogueModal product={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </main>
  );
};
